import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Link2, Loader, Plug, Unlink } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import {
  importUpworkMcpProfile,
  getUpworkMcpStatus,
  connectUpworkMcp,
  disconnectUpworkMcp,
  setUpworkMcpDefaultAccount,
} from '../services/upworkMcpService';

const STATUS_BADGES = {
  connected: { className: 'bg-green-100 text-green-700', label: 'Connected' },
  needs_reauth: { className: 'bg-amber-100 text-amber-700', label: 'Reconnect needed' },
  disconnected: { className: 'bg-gray-100 text-gray-700', label: 'Not connected' },
  not_connected: { className: 'bg-gray-100 text-gray-700', label: 'Not connected' },
};

function isSafeRedirect(url) {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

// Personal Upwork connection through Upwork's MCP server (RFC-002). Renders nothing until the
// backend reports the feature enabled for this user.
export default function UpworkMcpConnectionCard() {
  const { canReadUpworkCredentials, canUpdateUpworkCredentials, hasPermission } = usePermissions();
  const canRead = canReadUpworkCredentials();
  const canEdit = canUpdateUpworkCredentials();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await getUpworkMcpStatus());
    } catch {
      setStatus({ enabled: false });
    }
  }, []);

  useEffect(() => {
    if (canRead) loadStatus();
  }, [canRead, loadStatus]);

  useEffect(() => {
    const result = searchParams.get('upwork_mcp');
    const error = searchParams.get('upwork_mcp_error');
    if (!result && !error) return;

    setFeedback(
      error
        ? { tone: 'error', message: error }
        : { tone: 'success', message: 'Your Upwork account is connected through Upwork MCP.' }
    );
    const next = new URLSearchParams(searchParams);
    next.delete('upwork_mcp');
    next.delete('upwork_mcp_error');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnect = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const { authorizationUrl } = await connectUpworkMcp('/admin/integrations');
      if (!isSafeRedirect(authorizationUrl)) throw new Error('Upwork returned an invalid authorization URL.');
      window.location.assign(authorizationUrl);
    } catch (error) {
      setFeedback({ tone: 'error', message: error.message });
      setBusy(false);
    }
  };

  const handleImport = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      const result = await importUpworkMcpProfile(status.defaultOrgUid);
      setFeedback({ tone: result.failed ? 'error' : 'success',
        message: `Imported ${result.imported} profile items.${result.truncated ? " Some items are unsupported or exceed this import's size limit." : ''}${result.failed ? ` ${result.failed} items could not be saved; retry to complete the import.` : ''}` });
    } catch (error) {
      setFeedback({ tone: 'error', message: error.message });
    } finally { setBusy(false); }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await disconnectUpworkMcp();
      await loadStatus();
      setFeedback({ tone: 'success', message: 'Upwork MCP connection removed.' });
    } catch (error) {
      setFeedback({ tone: 'error', message: error.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDefaultAccount = async (event) => {
    const orgUid = event.target.value;
    setBusy(true);
    try {
      await setUpworkMcpDefaultAccount(orgUid);
      setStatus((current) => ({ ...current, defaultOrgUid: orgUid }));
    } catch (error) {
      setFeedback({ tone: 'error', message: error.message });
    } finally {
      setBusy(false);
    }
  };

  if (!canRead || !status?.enabled) return null;

  const badge = STATUS_BADGES[status.status] || STATUS_BADGES.not_connected;
  const isConnected = status.status === 'connected';

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Plug className="h-5 w-5 text-primary-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Upwork MCP (beta)</h3>
            <p className="text-sm text-gray-500">
              Connect your own Upwork account through Upwork&apos;s official MCP server. No API keys needed.
            </p>
          </div>
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.className}`}>
          {badge.label}
        </span>
      </div>

      {feedback && (
        <div
          className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
            feedback.tone === 'error'
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          {feedback.tone === 'error' ? <AlertCircle className="h-3.5 w-3.5 mt-0.5" /> : <CheckCircle className="h-3.5 w-3.5 mt-0.5" />}
          <span>{feedback.message}</span>
        </div>
      )}

      {status.status === 'needs_reauth' && (
        <p className="text-sm text-amber-700">Upwork ended this connection. Reconnect to keep using live Upwork data.</p>
      )}

      {isConnected && status.accounts?.length > 0 && (
        <label className="block text-sm text-gray-700">
          <span className="block mb-1 font-medium">Default Upwork account</span>
          <select
            value={status.defaultOrgUid || ''}
            onChange={handleDefaultAccount}
            disabled={!canEdit || busy}
            className="w-full py-2 px-3 text-sm rounded-md border border-gray-300 focus:border-primary-600 focus:outline-none"
          >
            {status.accounts.map((account) => (
              <option key={account.orgUid} value={account.orgUid}>
                {account.name} ({account.roleLabel || account.role})
              </option>
            ))}
          </select>
        </label>
      )}

      {isConnected && hasPermission('vector_data:create') && hasPermission('vector_data:update') && (
        <div>
        <button type="button" onClick={handleImport} disabled={busy}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50">
          {busy ? 'Working…' : 'Import into workspace knowledge'}
        </button>
        <p className="mt-1 text-xs text-gray-500">Adds profile and portfolio facts to shared proposal knowledge. Earlier imports are kept.</p>
        </div>
      )}

      {canEdit && (
        <div className="flex items-center gap-2">
          {!isConnected && (
            <button
              type="button"
              onClick={handleConnect}
              disabled={busy}
              className="inline-flex items-center gap-1.5 py-1.5 px-3 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
              {status.status === 'needs_reauth' ? 'Reconnect Upwork' : 'Connect Upwork'}
            </button>
          )}
          {status.status !== 'not_connected' && status.status !== 'disconnected' && (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={busy}
              className="inline-flex items-center gap-1.5 py-1.5 px-3 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Unlink className="h-3.5 w-3.5" />
              Disconnect
            </button>
          )}
        </div>
      )}
    </div>
  );
}
